import React from "react";
import { useTranslation } from "react-i18next";
import { Container, Grid, Box, Typography, Card } from "@mui/material";
import withTitle from "../../../components/withTitle/withTitle";
import style from "../../patients-module/patients/patients.module.scss";
import styles from "./patient-survey-report.module.scss";
import CssBaseline from "@mui/material/CssBaseline";
import SurveyReportGridViewToggle from "../../../components/GridViewToggle/SurveyReportGridViewToggle";
import BarChartIcon from "@mui/icons-material/BarChart";
import FiltersAccordion from "./filters-accordion/FiltersAccordion";
import MetricsSection from "./metrics-section/MetricsSection";
import BreakdownSection from "./breakdown-section/BreakdownSection";
import IssuesAndFeedbackSection from "./issues-and-feedback-section/IssuesAndFeedbackSection";
import DetailsView from "./details-view/DetailsView";
import useSurveyReportData from "./useSurveyReportData";
import { LineOfBusinessEnum } from '../../../constants/BusinessConstants';
import {
    ORDER_TYPE_OPTIONS,
    CARD_TITLES,
    CARD_DESCRIPTIONS,
    VIEW_STATES,
} from "../../../constants/PatientSurveyReportConstants";

const PatientSurveyReport = () => {
    const { t } = useTranslation();
    localStorage.setItem("drawerSelectedItem", "Reports");

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        activeTimeframe,
        setActiveTimeframe,
        optionsLocation,
        selectedDate,
        setSelectedDate,
        validDate,
        setValidDate,
        isAccordionOpen,
        setIsAccordionOpen,
        isGrid,
        setGridView,
        reportData,
        loading,
        view,
        detailsMode,
        setDetailsMode,
        detailsPage,
        setDetailsPage,
        detailsPageSize,
        setDetailsPageSize,
        detailsTotal,
        detailsRows,
        detailsLoading,
        periodStart,
        periodEnd,
        hasMetricsForPeriod,
        categories,
        topIssues,
        hasIssues,
        hasFeedbackHighlights,
        averageRatingTrend,
        totalResponsesTrend,
        satisfactionRateTrend,
        issuesReportedTrend,
        fetchSurveyReport,
        fetchSurveyDetails,
        handleChangePatientsFilter,
        formatBranchChip,
        accordionRef,
    } = useSurveyReportData();

    return (
        <div data-testid="PatientSurveyReport" className={styles.patientSurveyReport}>
            <Box sx={{ display: "flex" }}>
                <CssBaseline />
                <Container className="mainContainer">
                    <Grid container className={style.margin_top_bottom_10px}>
                        <Grid item sm={6}>
                            <div className={style.mainHeadingText}>
                                {`${LineOfBusinessEnum.patientToMember} Survey Report`}
                            </div>
                        </Grid>
                    </Grid>

                    <Grid
                        item
                        container
                        className={style.margin_top_bottom_10px}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                    >
                        <Grid item md={8} xs={12}>
                            <FiltersAccordion
                                filters={filters}
                                setFilters={setFilters}
                                appliedFilters={appliedFilters}
                                setAppliedFilters={setAppliedFilters}
                                activeTimeframe={activeTimeframe}
                                setActiveTimeframe={setActiveTimeframe}
                                optionsLocation={optionsLocation}
                                selectedDate={selectedDate}
                                setSelectedDate={setSelectedDate}
                                validDate={validDate}
                                setValidDate={setValidDate}
                                isAccordionOpen={isAccordionOpen}
                                setIsAccordionOpen={setIsAccordionOpen}
                                isGrid={isGrid}
                                setDetailsPage={setDetailsPage}
                                fetchSurveyReport={fetchSurveyReport}
                                fetchSurveyDetails={fetchSurveyDetails}
                                formatBranchChip={formatBranchChip}
                                handleChangePatientsFilter={handleChangePatientsFilter}
                                ORDER_TYPE_OPTIONS={ORDER_TYPE_OPTIONS}
                                style={style}
                                styles={styles}
                                accordionRef={accordionRef}
                            />
                        </Grid>

                        <Grid
                            item
                            md={4}
                            xs={12}
                            className={style.mainHeading}
                            sx={{
                                marginTop: { xs: "20px", md: 0 },
                                marginBottom: { xs: "10px", md: 0 },
                            }}
                        >
                            <SurveyReportGridViewToggle
                                isGrid={isGrid}
                                setGridView={(value) => {
                                    setGridView(value);
                                    if (!value) setDetailsPage(0);
                                }}
                                testId="recurring_toggle_button"
                            />
                        </Grid>
                    </Grid>

                    {isGrid ? (
                        view === VIEW_STATES.LOADING ? (
                            <>
                                <MetricsSection
                                    reportData={reportData}
                                    hasMetricsForPeriod={hasMetricsForPeriod}
                                    periodStart={periodStart}
                                    periodEnd={periodEnd}
                                    averageRatingTrend={averageRatingTrend}
                                    totalResponsesTrend={totalResponsesTrend}
                                    satisfactionRateTrend={satisfactionRateTrend}
                                    issuesReportedTrend={issuesReportedTrend}
                                    styles={styles}
                                    activeTimeframe={activeTimeframe}
                                    loading={true}
                                />

                                <BreakdownSection
                                    reportData={reportData}
                                    appliedFilters={appliedFilters}
                                    CARD_TITLES={CARD_TITLES}
                                    CARD_DESCRIPTIONS={CARD_DESCRIPTIONS}
                                    loading={true}
                                />

                                <IssuesAndFeedbackSection
                                    hasIssues={hasIssues}
                                    hasFeedbackHighlights={hasFeedbackHighlights}
                                    categories={categories}
                                    topIssues={topIssues}
                                    feedbackHighlights={reportData?.feedbackHighlights ?? []}
                                    loading={true}
                                />
                            </>
                        ) :
                            view === VIEW_STATES.EMPTY ? (
                                <Card className={styles.emptyStateCard}>
                                    <Box className={styles.emptyStateBox}>
                                        <BarChartIcon className={styles.emptyIcon} />
                                        <Typography variant="h6">
                                            {t("reports.patientSurveyReportPage.empty.noData")}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("reports.patientSurveyReportPage.empty.tryAdjusting")}
                                        </Typography>
                                    </Box>
                                </Card>
                            ) :
                                view === VIEW_STATES.ERROR ? (
                                    <Card className={styles.errorCard}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("reports.patientSurveyReportPage.empty.tryAgain")}
                                        </Typography>
                                    </Card>
                                ) :
                                    (
                                        <>
                                            <MetricsSection
                                                reportData={reportData}
                                                hasMetricsForPeriod={hasMetricsForPeriod}
                                                periodStart={periodStart}
                                                periodEnd={periodEnd}
                                                averageRatingTrend={averageRatingTrend}
                                                totalResponsesTrend={totalResponsesTrend}
                                                satisfactionRateTrend={satisfactionRateTrend}
                                                issuesReportedTrend={issuesReportedTrend}
                                                styles={styles}
                                                activeTimeframe={activeTimeframe}
                                                loading={view === VIEW_STATES.LOADING || loading}
                                            />

                                            <BreakdownSection
                                                reportData={reportData}
                                                appliedFilters={appliedFilters}
                                                CARD_TITLES={CARD_TITLES}
                                                CARD_DESCRIPTIONS={CARD_DESCRIPTIONS}
                                                loading={view === VIEW_STATES.LOADING || loading}
                                            />

                                            <IssuesAndFeedbackSection
                                                hasIssues={hasIssues}
                                                hasFeedbackHighlights={hasFeedbackHighlights}
                                                categories={categories}
                                                topIssues={topIssues}
                                                feedbackHighlights={reportData?.feedbackHighlights ?? []}
                                                loading={view === VIEW_STATES.LOADING || loading}
                                            />
                                        </>
                                    )
                    ) : (
                        <DetailsView
                            detailsMode={detailsMode}
                            setDetailsMode={setDetailsMode}
                            detailsRows={detailsRows}
                            detailsLoading={detailsLoading}
                            detailsPage={detailsPage}
                            setDetailsPage={setDetailsPage}
                            detailsPageSize={detailsPageSize}
                            setDetailsPageSize={setDetailsPageSize}
                            detailsTotal={detailsTotal}
                            styles={styles}
                        />
                    )}

                </Container>
            </Box>
        </div>
    );
};

export default withTitle(
    PatientSurveyReport,
    `${LineOfBusinessEnum.patientToMember} Survey Report`
);
